import { DragEvent, RefObject } from 'react';
import { Box, styled } from '@mui/material';
import * as fabric from 'fabric';
import { FabricJSCanvas } from 'fabricjs-react';
import { Point, Size } from 'types/issue';

type FloorPlanStageProps = {
  containerRef: RefObject<HTMLDivElement>;
  imageUrl: string;
  base: Size;
  pan: Point;
  scale: number;
  isDropTarget: boolean;
  canPan: boolean;
  onCanvasReady: (canvas: fabric.Canvas) => void;
  onNaturalSize: (size: Size) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onMarkerLayerMouseLeave: () => void;
  onImageLoad: () => void;
};

/**
 * Canvas area: the floor-plan image (100% = fit to screen) with the fabric
 * marker canvas overlaid on it. The markers live on their own unscaled canvas
 * rather than inside the CSS-scaled stage, which is what keeps them a constant
 * on-screen size at any zoom - only their position is recomputed from the
 * current pan/zoom.
 */
export const FloorPlanStage = ({
  containerRef,
  imageUrl,
  base,
  pan,
  scale,
  isDropTarget,
  canPan,
  onCanvasReady,
  onNaturalSize,
  onDragOver,
  onDragLeave,
  onDrop,
  onMarkerLayerMouseLeave,
  onImageLoad
}: FloorPlanStageProps) => {
  return (
    <StageArea>
      <CanvasBox
        ref={containerRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={{
          borderColor: isDropTarget ? '#226AAA' : '#E4E7EC',
          boxShadow: isDropTarget ? '0 0 0 3px rgba(34, 106, 170, 0.18)' : 'none',
          cursor: canPan ? 'grab' : 'default'
        }}>
        {base.w > 0 && (
          <ImageStage
            style={{
              width: base.w,
              height: base.h,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`
            }}>
            <FloorPlanImage
              src={imageUrl}
              alt="Floor plan"
              draggable={false}
              onLoad={(e) => {
                onNaturalSize({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight
                });
                onImageLoad?.();
              }}
            />
          </ImageStage>
        )}

        {/* Preload the natural size even before the stage renders. */}
        {base.w === 0 && (
          <HiddenImage
            src={imageUrl}
            alt=""
            onLoad={(e) => {
              onNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
              onImageLoad?.();
            }}
          />
        )}

        {/* Leaving the canvas altogether also clears the hovered marker. */}
        <MarkerLayer onMouseLeave={onMarkerLayerMouseLeave}>
          <MarkerCanvas onReady={onCanvasReady} />
        </MarkerLayer>
      </CanvasBox>
    </StageArea>
  );
};

const StageArea = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
}));

const CanvasBox = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  transition: 'border-color 120ms ease, box-shadow 120ms ease'
}));

const ImageStage = styled(Box)(() => ({
  position: 'relative',
  flexShrink: 0
}));

const FloorPlanImage = styled('img')(() => ({
  display: 'block',
  width: '100%',
  height: '100%',
  userSelect: 'none'
}));

const HiddenImage = styled('img')(() => ({
  display: 'none'
}));

const MarkerLayer = styled(Box)(() => ({
  position: 'absolute',
  inset: 0
}));

const MarkerCanvas = styled(FabricJSCanvas)(() => ({
  width: '100%',
  height: '100%'
}));
